import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CardProps } from '@mui/material/Card';
import { formatNumber, fNumber, fPercent } from 'src/utils/format-number';
import { bgGradient } from 'src/theme/css';
import Iconify from 'src/components/iconify';
import Chart, { useChart } from 'src/components/chart';
import { useResponsive } from 'src/hooks/use-responsive';
import { useSelector } from 'react-redux';
import { useLocales } from 'src/locales';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { formatCurrency } from 'src/utils/format-currency';

interface Props extends CardProps {
  title: string;
  total: any;
  percent: number;
  color?: string; // Accepts both ColorSchema or hex
  textColor?: string;
  compareData?: number;
  icon: string;
  spanColor: any;
  chart: {
    series: {
      x: any;
      y: any;
    }[];
    options?: any;
  };
  invertedColor?: boolean; // <= Ici
}

export default function BankingWidgetSummary({
  title,
  total,
  compareData,
  icon,
  textColor,
  spanColor,
  percent,
  color = 'primary',
  chart,
  sx,
  invertedColor = false, // <= Ici
  ...other
}: Props) {
  const theme: any = useTheme();
  const lgUp = useResponsive('up', 'lg');
  const filterStore = useSelector((state: any) => state.filters);
  const { t } = useLocales();
  const workspace = useCurrentWorkspace();
  const lang = localStorage.getItem('i18nextLng') || 'us-US';
  const workspace_currency = workspace?.currency?.toUpperCase() || 'USD';

  const isHexColor = typeof color === 'string' && color.startsWith('#');
  const paletteColor = !isHexColor ? theme.palette[color] : undefined;

  const gradientStart = alpha(isHexColor ? color : paletteColor?.light ?? paletteColor?.main, 0.2);
  const gradientEnd = alpha(isHexColor ? color : paletteColor?.main, 0.2);
  const chartColor = isHexColor ? color : paletteColor?.main;

  const chartOptions = useChart({
    chart: { sparkline: { enabled: true } },
    series: [{ data: chart.series }],
    xaxis: { labels: { show: true } },
    yaxis: { labels: { show: true } },
    legend: { show: true },
    grid: { show: true },
    tooltip: {
      marker: { show: false },
      y: {
        formatter: (value: number) => {
          if (title === t('dashboard.brandTraffic.blendedCtr')) {
            return `${Math.min(value, 100).toFixed(1)}%`;
          }
          if (title === t('dashboard.revenue')) {
            return formatCurrency(value ?? 0, workspace_currency, lang, true, 2);
          }
          if (title === t('dashboard.brandTraffic.blendedCpc')) {
            return formatCurrency(value ?? 0, workspace_currency, lang, true, 2);
          }
          if (title === t('dashboard.global.ad_cost')) {
            return formatCurrency(value ?? 0, workspace_currency, lang, true, 2);
          }
          return formatNumber(value); // fallback générique
        },
        title: { formatter: () => '' },
      },
    },
    fill: { colors: [chartColor] },
    colors: [chartColor],
    ...chart.options,
  });

  let trendIcon = '';
  let trendColor = '';

  if (compareData !== undefined && !isNaN(compareData) && isFinite(compareData)) {
    // > 0  = up  |  < 0 = down
    if (compareData > 0) {
      trendIcon = invertedColor ? 'eva:trending-up-fill' : 'eva:trending-up-fill';
      trendColor = invertedColor ? '#a81b21' : '#065e49'; // rouge si inversé, vert sinon
    } else if (compareData < 0) {
      trendIcon = invertedColor ? 'eva:trending-down-fill' : 'eva:trending-down-fill';
      trendColor = invertedColor ? '#065e49' : '#a81b21'; // vert si inversé, rouge sinon
    }
  }

  const formatTitle = (title: string, total: number) => {
    const formatted: any = {
      'dashboard.global.blended_cpl': () => formatCurrency(total, workspace_currency, lang, true, 0),
      'dashboard.global.blended_revenue': () => formatCurrency(total, workspace_currency, lang, true, 0),
      'dashboard.global.savings': () => formatCurrency(total, workspace_currency, lang, true, 1),
      'dashboard.global.ad_cost': () => formatCurrency(total, workspace_currency, lang, true, 1),
      'dashboard.global.blended_cpo': () => formatCurrency(total, workspace_currency, lang, true, 0),
      'dashboard.global.blended_cpc': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'Ad Cost without Cross-Brand': () => formatCurrency(total, workspace_currency, lang, true, 1),
      'Ad Cost with Cross-Brand': () => formatCurrency(total, workspace_currency, lang, true, 1),
      'dashboard.revenue': () => formatCurrency(total, workspace_currency, lang, true, 1),
      'Brand Organic Revenue': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'Brand Paid Revenue': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'Paid Revenue': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'dashboard.brandTraffic.totalBrandTraffic': () => formatNumber(total),
      'Organic Revenue': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'dashboard.brandTraffic.blendedCpc': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'dashboard.brandTraffic.blendedCtr': () => {
        const capped = Math.min(total, 100);
        return capped === 0 ? '0.0%' : fPercent(capped);
      },
      'dashboard.global.blended_leads': () => formatNumber(total),
      'dashboard.brandOverview.conversions': () => formatNumber(total),

      'dashboard.global.custom_based_revenue': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'dashboard.global.total_search_revenue': () => formatCurrency(total, workspace_currency, lang, true, 2),
      'dashboard.global.brand_revenue_gads': () => formatCurrency(total, workspace_currency, lang, true, 2),

      'dashboard.global.custom_based_conversions': () => formatNumber(total),
      'dashboard.global.total_search_conversions': () => formatNumber(total),
      'dashboard.global.brand_conversions_gads': () => formatNumber(total),

      "N/A": () => formatNumber(total),
    };

    const matchedKey = Object.keys(formatted).find((key) => title === t(key));
    return matchedKey ? formatted[matchedKey]() : total >= 1 ? formatNumber(total) : fPercent(total);
  };

  const cappedSeries = chart.series && title === 'Blended ctr'
    ? chart.series.map((point) => ({ x: point.x, y: Math.min(point.y, 100) }))
    : chart.series;

  return (
    <Stack
      sx={{
        ...bgGradient({ direction: '135deg', startColor: gradientStart, endColor: gradientEnd }),
        width: 1,
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        color: theme.palette.mode !== 'dark' ? textColor : 'white',
        boxShadow:
          '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
        backgroundColor: isHexColor
          ? color
          : theme.palette.mode === 'dark'
            ? paletteColor?.darker
            : paletteColor?.lighter,
        ...sx,
      }}
      {...other}
    >
      {lgUp && (
        <Iconify
          icon={icon}
          sx={{
            transform:
              icon === 'solar:magnet-wave-bold-duotone' ||
                icon === 'solar:hand-money-bold-duotone'
                ? 'scaleX(-1)'
                : icon === 'icon-park-twotone:search' || icon === 'icon-park-twotone:click'
                  ? 'rotate(80deg)'
                  : undefined,
            width: 80,
            right: -20,
            top: 20,
            height: 80,
            opacity: 0.5,
            position: 'absolute',
          }}
        />
      )}

      <Stack sx={{ p: 2 }}>
        <Typography sx={{ fontSize: 12 }} variant="subtitle2">
          {title.toUpperCase()}
        </Typography>

        <Typography variant="h3">{formatTitle(title, total)}</Typography>

        <Stack
          spacing={0.5}
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          sx={{ typography: 'body2', fontWeight: 'bold', whiteSpace: 'nowrap' }}
        >
          {filterStore?.compare !== false &&
            total !== 0 &&
            compareData !== undefined &&
            !isNaN(compareData) &&
            isFinite(compareData) && (
              <Box
                sx={{ display: 'flex', flexDirection: 'row', gap: 1, height: '100%' }}
              >
                <div
                  style={{
                    backgroundColor: spanColor,
                    padding: '1px 4px',
                    display: 'flex',
                    flexDirection: 'row',
                    borderRadius: 10,
                  }}
                >
                  <Iconify fontSize={{ xs: 10, lg: 15 }} icon={trendIcon} sx={{ color: trendColor }} />
                  <Box
                    fontSize={{ xs: 12, lg: 15 }}
                    sx={{ opacity: 0.8, color: trendColor, whiteSpace: 'nowrap' }}
                  >
                    {fPercent(compareData || 0)}
                  </Box>
                </div>
                <Box fontSize={{ xs: 12, lg: 15 }} sx={{ opacity: 0.8, whiteSpace: 'nowrap' }}>
                  vs comp. period
                </Box>
              </Box>
            )}
        </Stack>
      </Stack>

      {total !== 0 && chart.series?.length > 0 ? (
        <Chart type="area" series={[{ data: cappedSeries }]} options={chartOptions} height={40} />
      ) : (
        <div style={{ height: 40 }} />
      )}
    </Stack>
  );
}
